import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  closeTransfer,
  closeTransferForced,
  deleteTransfer,
  getTransfersById,
  getTransferStockById,
  postTransferStockById,
  transitTransfer,
} from '../services/transfersService';
import { instance } from '../services/api';

// Las acciones de close/reject-all y receive y reopen aún no estaban en
// transfersService.js — añado wrappers locales aquí para no obligar a tocar
// otros consumidores. Si se usan desde más sitios, se promueven al service.
const closeTransferRejectAll = (id) => instance.post(`/api/v1/transfers/${id}/close/reject-all`, {});
const reopenTransfer = (id) => instance.post(`/api/v1/transfers/${id}/reopen`, {});
const receiveTransferStock = (id, payload) => instance.post(`/api/v1/transfers/${id}/receive`, payload);

// Claves de cache. Centralizadas para que el invalidate de cualquier mutación
// las encuentre sin construir tuplas a mano.
export const transferKeys = {
  detail: (id) => ['transfer', Number(id)],
  stock: (id, args) => ['transfer-stock', Number(id), args || {}],
};

const invalidateTransfer = (queryClient, id) => {
  // Usamos refetchQueries en lugar de invalidateQueries: forzamos el refetch
  // incondicional de detalle y stock. invalidateQueries marca como stale y
  // confía en que la query observada haga el refetch en background, lo cual
  // depende de heurísticas (staleTime, focus, mounted). En el caso del
  // detalle de transfer y su stock, hemos visto que tras una mutación rápida
  // (receive, accept-all, close) el refetch no siempre dispara y la pantalla
  // se queda con datos viejos. refetchQueries elimina la duda.
  queryClient.refetchQueries(transferKeys.detail(id));
  queryClient.refetchQueries(['transfer-stock', Number(id)]);
};

/**
 * Detalle de la transferencia. Se invalida automáticamente tras cualquier
 * acción que cambie su estado o su stock.
 */
export const useTransferQuery = (id) =>
  useQuery(transferKeys.detail(id), () => getTransfersById(id).then((r) => r.data), {
    enabled: Boolean(id),
    staleTime: 30_000,
  });

/**
 * Stock paginado de la transferencia. `args` admite page, limit, search,
 * filtros taxonómicos. Devuelve `{ data, metadata }` tal cual del back.
 */
export const useTransferStockQuery = (id, args) =>
  useQuery(transferKeys.stock(id, args), () => getTransferStockById(id, args).then((r) => r.data), {
    enabled: Boolean(id),
    keepPreviousData: true,
  });

/**
 * Mutaciones de cambio de estado. Cada una invalida detalle y stock al éxito.
 *
 * Las firmas de mutate aceptan solo el `id` o `{id, payload}` cuando aplica:
 *   transit.mutate(id)
 *   close.mutate(id)
 *   acceptAll.mutate(id)
 *   rejectAll.mutate(id)
 *   reopen.mutate(id)
 *   receive.mutate({ id, items })   // items: [{barcode, consolidatedQty}]
 *   addStock.mutate({ id, items })  // items: array compatible con joiValidatorStock
 *   deleteOne.mutate(id)
 */
export const useTransferMutations = (id) => {
  const queryClient = useQueryClient();
  const onSuccess = () => invalidateTransfer(queryClient, id);

  return {
    transit: useMutation(() => transitTransfer(id), { onSuccess }),
    close: useMutation(() => closeTransfer(id), { onSuccess }),
    acceptAll: useMutation(() => closeTransferForced(id), { onSuccess }),
    rejectAll: useMutation(() => closeTransferRejectAll(id), { onSuccess }),
    reopen: useMutation(() => reopenTransfer(id), { onSuccess }),
    receive: useMutation((items) => receiveTransferStock(id, items), { onSuccess }),
    addStock: useMutation((items) => postTransferStockById(id, items), { onSuccess }),
    deleteOne: useMutation(() => deleteTransfer(id), {
      // Después de borrar el detalle ya no existe; invalidamos solo el listado.
      onSuccess: () => queryClient.invalidateQueries(['transfers']),
    }),
  };
};
